import React, { lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { UserDetailsContext, useUserDetailsContext } from 'context/userDetailsContext';
import { useDebounce } from 'hooks/useDebounce';
import { MAPPING_STATUSES } from 'constants/mappingStatus';
import { listGroupedFiles } from 'services/fileManager/fileManagerServices';
import { getDateBoundaryInTimezone } from 'utils/utility_functions/timezone';
import { sortByKey } from 'utils/utils';
import { totalCountEvent } from 'services/generalServices';
import { listUsers } from 'services/users/userServices';
import MailListV3 from './MailListV3';
import useEmailThreadsV3 from './hooks/useEmailThreadsV3';
import useEmailThreadDetailsV3 from './hooks/useEmailThreadDetailsV3';
import useMarkEmailsReadV3 from './hooks/useMarkEmailsReadV3';
import '../mailboxV3.scss';

const MailPreviewV3 = lazy(() => import('./MailPreviewV3'));
const MailCreateModal = lazy(() => import('./MailCreateModal'));
const CaseMappingModal = lazy(() => import('../reconciliation/Components/CaseMappingModal'));

const ALL_MAPPING_STATUSES = MAPPING_STATUSES.filter((s) => s.key !== 5).map((s) => s.value);

const FILTER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
];

const MAPPING_FILTER_OPTIONS = [
  { key: 'ai', label: 'Mapped by AI', dotClass: 'dot-blue', statuses: ['mapped_by_ai'] },
  { key: 'user', label: 'Mapped by User', dotClass: 'dot-green', statuses: ['mapped_by_user'] },
  { key: 'lawft', label: 'Originated in Lawft', dotClass: 'dot-purple', statuses: ['originated_in_lawft'] },
  { key: 'unmapped', label: 'Unmapped by AI', dotClass: 'dot-gray', statuses: ['unmapped_by_ai', 'ai_mapping_failed'] },
  { key: 'failed_mail', label: 'Failed Mail Deliveries', dotClass: 'dot-red', statuses: ['failed_mail_deliveries'] },
];

/**
 * Case- or lead-scoped mailbox using the same Mail V3 shell as ReconciliationPageV3
 * (filters, virtualized list, MailPreviewV3), with threads restricted to the current case/lead.
 * Supports the same My Mail / Entire Firm scope as global mail (user filter + firm email log).
 */
function MailBoxV3({ caseDetails }) {
  const userContext = useContext(UserDetailsContext);
  const { userDetails } = useUserDetailsContext();
  const calendarRef = useRef(null);

  const caseId = caseDetails?.case_id;
  const caseName = caseDetails?.case_description?.case_name;
  const isLead = caseDetails?.is_lead ?? false;

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 992);
  const [mobileView, setMobileView] = useState('list');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [viewIndex, setViewIndex] = useState(0);
  const isSent = viewIndex === 1;

  /** My Mail = current user only; Entire Firm = firm-wide (optional By User multi-select). */
  const [section, setSection] = useState('my');
  const [userList, setUserList] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const [readScope, setReadScope] = useState('all');
  const [mappingSubFilter, setMappingSubFilter] = useState(null);

  const [selectedEmails, setSelectedEmails] = useState([]);
  const [showMapping, setShowMapping] = useState(false);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const searchKeywordDebounced = useDebounce(searchKeyword, 500);
  const [dateFilter, setDateFilter] = useState('');
  const [existingFiles, setExistingFiles] = useState([]);
  const [selectedThreadPkSk, setSelectedThreadPkSk] = useState({ pk: null, sk: null });
  const [readEmails, setReadEmails] = useState({});

  const { mappingStatuses, isUnread } = useMemo(() => {
    const matchingSub = MAPPING_FILTER_OPTIONS.find((f) => f.key === mappingSubFilter);
    const statuses = matchingSub ? matchingSub.statuses : ALL_MAPPING_STATUSES;
    return { mappingStatuses: statuses, isUnread: readScope === 'unread' };
  }, [readScope, mappingSubFilter]);

  const userFilter = useMemo(() => {
    if (section === 'my') return userDetails?.firm_user_id ? [userDetails.firm_user_id] : [];
    return selectedUserIds;
  }, [section, selectedUserIds, userDetails]);

  const [filters, setFilters] = useState({
    mail_date_lower: '',
    mail_date_upper: '',
  });

  useEffect(() => {
    const dateArray = dateFilter ? (Array.isArray(dateFilter) ? dateFilter : [dateFilter]) : [];
    const mail_date_lower = dateArray[0] ? getDateBoundaryInTimezone(dateArray[0], userContext?.userDetails?.timezone, 'start') : '';
    const mail_date_upper = dateArray[0]
      ? getDateBoundaryInTimezone(dateArray[1] ?? dateArray[0], userContext?.userDetails?.timezone, 'end')
      : '';
    setFilters({ mail_date_lower, mail_date_upper });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter]);

  const memoizedFilters = useMemo(
    () => ({
      ...filters,
      caseId: caseId ?? '',
      mappingStatuses,
      userFilter,
    }),
    [filters, caseId, mappingStatuses, userFilter]
  );

  const mailViewType = section === 'firm' ? 'entire_firm' : 'my_email';

  const { threads, loading, paginationLoading, hasMore, totalCount, handleLoadMore, refresh } = useEmailThreadsV3({
    searchKeyword: searchKeywordDebounced,
    isSent,
    isUnread,
    filters: memoizedFilters,
    limit: 20,
    isEmailLog: section === 'firm',
    enabled: Boolean(caseId),
    viewType: mailViewType,
  });

  const visibleThreads = isUnread ? threads.filter((t) => !readEmails[t.pk]) : threads;

  /** Unread filter: pagination total_count does not drop until refetch; align label with local read state. */
  const listTotalCount = useMemo(() => {
    if (!isUnread) return totalCount;
    const readLocallyStillUnreadInList = threads.filter((t) => Boolean(t.is_unread) && readEmails[t.pk]).length;
    return Math.max(0, (totalCount ?? 0) - readLocallyStillUnreadInList);
  }, [isUnread, totalCount, threads, readEmails]);

  const { emailDetails, loading: detailLoading } = useEmailThreadDetailsV3(selectedThreadPkSk, setReadEmails);

  const markReadFilterKey = useMemo(
    () => ({
      searchKeyword: searchKeywordDebounced,
      isSent,
      isUnread: isUnread || undefined,
      isEmailLog: section === 'firm',
      viewType: mailViewType,
      caseId: memoizedFilters.caseId,
      mailDateLower: memoizedFilters.mail_date_lower,
      mailDateUpper: memoizedFilters.mail_date_upper,
      mappingStatus: memoizedFilters.mappingStatuses?.join(','),
      filterByUser: memoizedFilters.userFilter?.join(','),
      limit: 20,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchKeywordDebounced, isSent, isUnread, section, mailViewType, JSON.stringify(memoizedFilters)]
  );

  const { markRead, markUnread } = useMarkEmailsReadV3(markReadFilterKey);

  const { bulkMarkAsUnread, bulkReadUnreadLabel } = useMemo(() => {
    if (!selectedEmails?.length) {
      return { bulkMarkAsUnread: false, bulkReadUnreadLabel: 'Mark as read' };
    }
    const localRead = readEmails ?? {};
    const allEffectivelyRead = selectedEmails.every((email) => {
      const thread = visibleThreads.find((t) => t.pk === email.pk && t.sk === email.sk);
      if (!thread) return false;
      const effectivelyUnread = Boolean(thread.is_unread) && !localRead[thread.pk];
      return !effectivelyUnread;
    });
    return {
      bulkMarkAsUnread: allEffectivelyRead,
      bulkReadUnreadLabel: allEffectivelyRead ? 'Mark as unread' : 'Mark as read',
    };
  }, [visibleThreads, readEmails, selectedEmails]);

  const handleTopbarBulkReadUnread = useCallback(() => {
    if (!markRead || !markUnread) return;
    const threadIds = selectedEmails
      .map((e) => {
        const thread = visibleThreads.find((t) => t.pk === e.pk && t.sk === e.sk);
        return thread?.thread_id ?? thread?.email_thread_id ?? e.thread_id;
      })
      .filter(Boolean);
    if (!threadIds.length) return;

    if (bulkMarkAsUnread) {
      markUnread(threadIds);
      setReadEmails((prev) => {
        const next = { ...prev };
        selectedEmails.forEach((e) => {
          delete next[e.pk];
        });
        return next;
      });
    } else {
      markRead(threadIds);
      setReadEmails((prev) => {
        const next = { ...prev };
        selectedEmails.forEach((e) => {
          next[e.pk] = true;
        });
        return next;
      });
    }
  }, [markRead, markUnread, visibleThreads, selectedEmails, bulkMarkAsUnread, setReadEmails]);

  const selectedThreadMeta = useMemo(
    () =>
      visibleThreads.find((t) => t.pk === selectedThreadPkSk?.pk && String(t.sk ?? '') === String(selectedThreadPkSk?.sk ?? '')) ??
      visibleThreads.find((t) => t.pk === selectedThreadPkSk?.pk) ??
      null,
    [visibleThreads, selectedThreadPkSk]
  );

  const handleEmailData = useCallback(() => {
    if (isSent) refresh();
  }, [isSent, refresh]);

  const handleSearch = useCallback((e) => {
    setSearchKeyword(e.target.value);
    setSelectedThreadPkSk({ pk: null, sk: null });
  }, []);

  useEffect(() => {
    totalCountEvent('user')
      .then((response) => {
        listUsers({
          status: 'ACTIVE',
          limit: response.data,
          is_confirmed_user: true,
          is_include_groups: false,
          active_groups: false,
          excludeFromLoading: true,
        })
          .then((res) => {
            const firmUsers = res.data?.firm_users?.filter((u) => u.res_type === 'user') || [];
            const transformed = firmUsers.map((u) => ({
              ...u,
              label: `${u.first_name} ${u.last_name}`,
              value: u.firm_user_id,
              initials: `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase(),
            }));
            setUserList(sortByKey(transformed, 'label'));
          })
          .catch((err) => console.error(err));
      })
      .catch((err) => console.warn(err));
  }, []);

  useEffect(() => {
    if (!caseId) return;
    listGroupedFiles({ case_id: caseId, all_files: true, excludeFromLoading: true })
      .then((res) => {
        const arr = Array.isArray(res?.data) ? res.data : [];
        setExistingFiles(
          arr.map((item) => ({
            ...item,
            label: item.display_name,
            value: {
              file_path: item.file_path,
              size: item.size,
              display_name: item.display_name,
              file_name: item.file_name,
              file_type: item.file_type,
              created_by: item.created_by_details,
              is_new_file: true,
              sk: item.sk,
              pk: item.pk,
            },
          }))
        );
      })
      .catch(() => {});
  }, [caseId]);

  const handleThreadSelect = useCallback(
    (pksk) => {
      setSelectedThreadPkSk(pksk);
      if (isMobile) setMobileView('detail');
    },
    [isMobile]
  );

  const handleBackToList = useCallback(() => {
    setMobileView('list');
    setSelectedThreadPkSk({ pk: null, sk: null });
  }, []);

  const handleViewChange = useCallback((idx) => {
    setViewIndex(idx);
    setSelectedThreadPkSk({ pk: null, sk: null });
    setSelectedEmails([]);
    setMobileView('list');
    if (idx === 1) {
      setReadScope('all');
      setMappingSubFilter(null);
    }
  }, []);

  const handleReadScopeChange = useCallback((key) => {
    setReadScope(key);
  }, []);

  const handleMappingSubFilterChange = useCallback((key) => {
    setMappingSubFilter((prev) => (prev === key ? null : key));
  }, []);

  const handleSectionChange = useCallback((s) => {
    setSection(s);
    setSelectedThreadPkSk({ pk: null, sk: null });
    setSelectedEmails([]);
    setReadScope('all');
    setMappingSubFilter(null);
    setSearchKeyword('');
    setMobileView('list');
  }, []);

  const firmUserItemTemplate = useCallback((option) => {
    if (!option) return null;
    return (
      <>
        {option.firm_user_id && <i className="fas fa-user-tie me-2" aria-hidden="true" />}
        {option.label}
      </>
    );
  }, []);

  const mailV3DatePicker = (
    <TimezoneDatePicker
      ref={calendarRef}
      timezone={userContext?.userDetails?.timezone}
      className={`mail-v3-date-picker${dateFilter ? ' has-date' : ''}`}
      inputClassName="mail-v3-date-input"
      id="mail-v3-case-date-filter"
      value={dateFilter}
      onChange={(value) => {
        setDateFilter(value);
        setSelectedThreadPkSk({});
      }}
      selectionMode="range"
      showIcon
      showButtonBar
      placeholder="Date range"
      isEndOfDay={true}
      readOnlyInput
    />
  );

  return (
    <div className="mail-v3" style={{ height: '100%', minHeight: '520px' }}>
      <div className="mail-v3-topbar">
        <div className="mail-v3-logo-pill">
          <h1 className="title gray-800 mb-0 fs-3 fs-md-1">Mail</h1>
        </div>
        <div className="mail-v3-topbar-divider" />
        <div className="mail-v3-section-switch">
          <button type="button" className={`mail-v3-sec-btn${section === 'my' ? ' active' : ''}`} onClick={() => handleSectionChange('my')}>
            My Mail
          </button>
          <button
            type="button"
            className={`mail-v3-sec-btn${section === 'firm' ? ' active' : ''}`}
            onClick={() => handleSectionChange('firm')}
          >
            Entire Firm
          </button>
        </div>

        <div className="mail-v3-topbar-right flex-wrap">
          <Button
            type="button"
            className="p-button p-component btn-outline p-button-secondary mail-v3-topbar-btn mail-v3-topbar-btn--text-only"
            onClick={handleTopbarBulkReadUnread}
            disabled={selectedEmails.length === 0}
            tooltip={bulkReadUnreadLabel}
            tooltipOptions={{ position: 'bottom', showDelay: 400 }}
          >
            <span className="mail-v3-btn-label">{bulkReadUnreadLabel}</span>
          </Button>
          <Button
            type="button"
            className="p-button p-component btn-outline p-button-secondary mail-v3-topbar-btn mail-v3-topbar-btn--text-only"
            onClick={() => setShowMapping(true)}
            disabled={selectedEmails.length === 0}
            tooltip="Map to Case/Lead"
            tooltipOptions={{ position: 'bottom', showDelay: 400 }}
          >
            <span className="mail-v3-btn-label">Map to Case/Lead</span>
          </Button>

          <Button
            type="button"
            className="p-button p-component p-button-primary mail-v3-topbar-btn mail-v3-topbar-btn--with-icon"
            onClick={() => setShowComposeModal(true)}
            tooltip="Compose"
            tooltipOptions={{ position: 'bottom', showDelay: 400 }}
          >
            <i className="pi pi-plus mail-v3-topbar-btn-icon" aria-hidden="true" />
            <span className="mail-v3-btn-label">Compose</span>
          </Button>
        </div>
      </div>
      <div className="d-flex align-items-center mb-1">
        <span className="d-flex p-1 align-items-center" style={{ lineHeight: '18px', fontSize: '12px' }}>
          <i className="pi pi-info-circle me-2"></i>
          AI-generated responses may contain errors or inaccuracies. You should not rely on AI responses without independent verification.
        </span>
      </div>
      <div className="mail-v3-main">
        <div className={`mail-v3-left-panel${isMobile && mobileView === 'detail' ? ' mail-v3-panel-hidden' : ''}`}>
          <div className="mail-v3-view-tabs">
            <button type="button" className={`mail-v3-view-tab${viewIndex === 0 ? ' active' : ''}`} onClick={() => handleViewChange(0)}>
              <i className="pi pi-inbox" aria-hidden="true" />
              Inbox{section === 'firm' ? ' — Entire Firm' : ''}
            </button>
            <button type="button" className={`mail-v3-view-tab${viewIndex === 1 ? ' active' : ''}`} onClick={() => handleViewChange(1)}>
              <i className="pi pi-send" aria-hidden="true" />
              Sent{section === 'firm' ? ' — Entire Firm' : ''}
            </button>
          </div>

          <div className="mail-v3-search-row">
            <div className="mail-v3-search-wrap">
              <i className="pi pi-search search-icon-v3" aria-hidden="true" />
              <TextSnippetWrapper
                type="text"
                name="keyword"
                inputClassName="w-100"
                className="w-100"
                placeholder="Search by name, subject…"
                value={searchKeyword}
                onChange={handleSearch}
                autoComplete="off"
              />
            </div>
          </div>

          {!isSent && (
            <div className="mail-v3-filter-row">
              {FILTER_OPTIONS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`mail-v3-chip${readScope === f.key ? ' on' : ''}`}
                  onClick={() => handleReadScopeChange(f.key)}
                >
                  {f.label}
                </button>
              ))}

              <div className="mail-v3-chip-sep" />
              {MAPPING_FILTER_OPTIONS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`mail-v3-chip${mappingSubFilter === f.key ? ' on' : ''}`}
                  onClick={() => handleMappingSubFilterChange(f.key)}
                >
                  <span className={`mail-v3-dot ${f.dotClass}`} />
                  {f.label}
                </button>
              ))}
            </div>
          )}
          <div className="d-flex align-items-center gap-2 mail-v3-filter-row-2">
            {section === 'firm' && (
              <div className="mail-v3-firm-user-ms-wrap">
                <MultiSelect
                  value={selectedUserIds}
                  options={userList}
                  onChange={(e) => {
                    setSelectedUserIds(e.value ?? []);
                    setSelectedThreadPkSk({ pk: null, sk: null });
                  }}
                  placeholder="By User"
                  className="input-shadow F-size14 mail-v3-firm-user-multiselect"
                  optionLabel="label"
                  optionValue="value"
                  display="chip"
                  filter
                  filterPlaceholder="Search users"
                  itemTemplate={firmUserItemTemplate}
                  showClear
                  maxSelectedLabels={3}
                />
              </div>
            )}
            <div className="mail-v3-date-wrap ms-auto">{mailV3DatePicker}</div>
          </div>

          <MailListV3
            threads={visibleThreads}
            totalCount={listTotalCount}
            selectedThreadPk={selectedThreadPkSk?.pk}
            selectedThreadSk={selectedThreadPkSk?.sk}
            setSelectedThreadPkSk={handleThreadSelect}
            handleLoadMore={handleLoadMore}
            loading={loading}
            hasMore={hasMore}
            paginationLoading={paginationLoading}
            readEmails={readEmails}
            setReadEmails={setReadEmails}
            multiple
            selectedEmails={selectedEmails}
            setSelectedEmails={setSelectedEmails}
            isUnreadTab={isUnread}
            isSentView={isSent}
            showDualFromToRow={section === 'firm'}
          />
        </div>

        <div className={`mail-v3-right-panel${isMobile && mobileView === 'list' ? ' mail-v3-panel-hidden' : ''}`}>
          {isMobile && mobileView === 'detail' && (
            <button type="button" className="mail-v3-back-btn" onClick={handleBackToList}>
              <i className="pi pi-arrow-left me-2" aria-hidden="true" />
              Back to inbox
            </button>
          )}

          {selectedThreadPkSk?.pk ? (
            <Suspense
              fallback={
                <div className="mail-v3-detail-skeleton">
                  <div className="mail-v3-thread-item-skeleton" style={{ padding: '24px' }}>
                    <div className="mail-v3-skeleton-line" style={{ width: '60%', height: 16, borderRadius: 6 }} />
                  </div>
                </div>
              }
            >
              <MailPreviewV3
                key={`${selectedThreadPkSk?.pk ?? ''}:${selectedThreadPkSk?.sk ?? ''}`}
                previewMail={emailDetails}
                loading={detailLoading}
                threadMeta={selectedThreadMeta}
                caseDetails={caseDetails}
                handleEmailData={handleEmailData}
                existingFiles={existingFiles}
                isFromMailLogs={false}
                setShowMapping={setShowMapping}
                threadPk={selectedThreadPkSk?.pk}
                threadSk={selectedThreadPkSk?.sk}
              />
            </Suspense>
          ) : (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 F-size16 start-conversation">
              <i className="pi pi-inbox empty-icon" aria-hidden="true" style={{ fontSize: '42px' }} />
              <p>Select an email to read it</p>
            </div>
          )}
        </div>
      </div>

      {showMapping && (
        <Suspense fallback={null}>
          <CaseMappingModal
            show={showMapping}
            setShow={setShowMapping}
            selectedEmails={selectedEmails}
            setSelectedEmails={setSelectedEmails}
            setSelectedEmailThreadPkSk={setSelectedThreadPkSk}
            refreshEmails={refresh}
            handleReconcile={refresh}
          />
        </Suspense>
      )}

      {showComposeModal && (
        <Suspense fallback={null}>
          <MailCreateModal
            setShow={setShowComposeModal}
            show={showComposeModal}
            caseId={caseId}
            caseName={caseName}
            isLead={isLead}
            handleSentEmailList={handleEmailData}
            existingFiles={existingFiles}
          />
        </Suspense>
      )}
    </div>
  );
}

MailBoxV3.propTypes = {
  caseDetails: PropTypes.shape({
    case_id: PropTypes.string.isRequired,
    is_lead: PropTypes.bool,
    case_description: PropTypes.shape({
      case_name: PropTypes.string,
    }),
  }).isRequired,
};

export default MailBoxV3;
